import React, { createContext, useContext, useState } from 'react';
import NotificationToast from './NotificationToast';

const NotificationContext = createContext();

/**
 * Custom hook to use the notification context
 * @returns {Object} Notification context with show methods
 */
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

/**
 * NotificationProvider component
 * Provides a context for showing notifications throughout the application
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.Component} NotificationProvider component
 */
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  
  /**
   * Show a success notification
   * @param {string} message - Notification message
   * @param {number} duration - Duration in ms
   */
  const showSuccess = (message, duration = 5000) => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, type: 'success', message, duration }]);
  };
  
  /**
   * Show an error notification
   * @param {string} message - Notification message
   * @param {number} duration - Duration in ms
   */
  const showError = (message, duration = 5000) => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, type: 'error', message, duration }]);
  };
  
  /**
   * Remove a notification by ID
   * @param {string} id - Notification ID
   */
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };
  
  return (
    <NotificationContext.Provider value={{ showSuccess, showError }}>
      {children}
      
      {/* Render notifications */}
      {notifications.map(notification => (
        <NotificationToast
          key={notification.id}
          type={notification.type}
          message={notification.message}
          duration={notification.duration}
          onClose={() => removeNotification(notification.id)}
          show={true}
        />
      ))}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider; 